import { useEffect } from 'react';

import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import {
  FEATURE_LIMIT_CHECK_SOURCE,
  FEATURE_LIMITS,
  featureHasUsages,
  getFeatureLimitsData,
} from 'src/data/featureLimits';
import { getCustomerPlan, isPlanFromEnterpriseCategory } from 'src/data/subscription';
import {
  shownPerformancePredictionsUpgradeTimestamp,
  wasPerformancePredictionsBannerClosedInLast24Hours,
  wasPerformancePredictionsUpgradeShownInLast24Hours,
} from 'src/lib/localData';
import { useCurrentCustomer, useCustomerActions, useUiActions } from 'src/store';
import { useUpgradePopup } from './popup';

export const CustomerFeatureLimitsGuard = () => {
  const customer = useCurrentCustomer();
  const customerActions = useCustomerActions();
  const uiActions = useUiActions();

  // Usages of "performancePredictions" feature should be changed only by generations,
  // so we can use "generationFlow" origin for "upgrade" popup - signalling that the popup was opened after generation flow
  const openUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.generationFlow,
  });

  useEffect(() => {
    // Following checks are only for non-Enterprise customers
    const plan = getCustomerPlan(customer);
    if (isPlanFromEnterpriseCategory(plan)) {
      return;
    }

    // React to "performance predictions" feature limit reached
    const featureLimitsData = getFeatureLimitsData(customer, FEATURE_LIMITS.performancePredictions);
    if (!featureHasUsages(featureLimitsData)) {
      // If user haven't seen the "upgrade popup" in the last 24h and last "limits refresh" was called from the "generation flow", show the popup ...
      if (
        !wasPerformancePredictionsUpgradeShownInLast24Hours() &&
        customerActions.checkLastFeatureLimitCheckSource(FEATURE_LIMIT_CHECK_SOURCE.generationFlow)
      ) {
        shownPerformancePredictionsUpgradeTimestamp.write(Date.now()); // Store the timestamp when the performance predictions upgrade popup was shown - we want to show it only once per 24h
        openUpgradePopup(FEATURE_LIMITS.performancePredictions);

        // ... else, if the user haven't closed the banner in the last 24h, show the banner
      } else if (!wasPerformancePredictionsBannerClosedInLast24Hours()) {
        uiActions.setShowPerformancePredictionsBanner(true);
      }
    } else {
      // Make sure we hide the banner if the feature limit is not reached
      uiActions.setShowPerformancePredictionsBanner(false);
    }
  }, [customer, uiActions, openUpgradePopup, customerActions]);

  return null;
};
